import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ContentCompetency, SidebarMenu } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/App';
import { SEARCH_PAGE_CATEGORIES } from '@backrs_ui/grow-ui/src/pages/Search/search.types';
import { fetchNuggetsUserHasSearched } from '@backrs_ui/grow-ui/src/pages/Search/search.util';
import { selectContentCompetencies } from '@backrs_ui/grow-ui/src/redux/contentCompetency/contentCompetency';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { setSearchTermForCategory } from '@backrs_ui/grow-ui/src/redux/search/search';
import { selectUser } from '@backrs_ui/grow-ui/src/redux/user/user';
import { useFetchUserPermissionsQuery } from '@backrs_ui/grow-ui/src/redux/user/user.api';
import { UIPermissionList } from '@backrs_ui/grow-ui/src/redux/user/user.transformer';

import { usePostHog } from 'posthog-js/react';

import { bottomSection } from './bottomSection';
import { middleSection } from './middleSection';
import { popupSectionMenuItems } from './popupSection';
import { topSection } from './topSection';

export type SectionDependencies = Readonly<{
  navigate: ReturnType<typeof useNavigate>;
  location: ReturnType<typeof useLocation>;
  dispatch: ReturnType<typeof useAppDispatch>;
  competencies?: ContentCompetency[];
  doesUserHaveBlueprint: boolean;
  userPermissions?: UIPermissionList;
  postHog: ReturnType<typeof usePostHog>;
  isUnauthenticated: boolean;
}>;

export const useMenuSections = (): SidebarMenu | undefined => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const postHog = usePostHog();

  const competencies = useAppSelector(selectContentCompetencies).contentCompetencies;
  const unauthenticatedUser = {
    id: 0,
    access_token: '',
    email: '',
    first_name: 'Guest',
    last_name: 'User',
    profile_image: undefined,
    location: undefined,
    job_title: undefined,
    catch_up_count: 0,
    isUnauthenticated: true,
  };
  const user = useAppSelector(selectUser) || unauthenticatedUser;

  const { data: userPermissions } = useFetchUserPermissionsQuery(undefined);

  const doesUserHaveBlueprint = useAppSelector(
    (state) => state.userResponseSet.mostRecentUserResponseSet?.user_responses,
  );

  return useMemo(() => {
    const handleSearch = (searchTerm?: string) => {
      if (searchTerm) {
        dispatch(
          setSearchTermForCategory({ category: SEARCH_PAGE_CATEGORIES.DEFAULT, searchTerm }),
        );

        fetchNuggetsUserHasSearched(dispatch, searchTerm, 1);
      }

      navigate(APP_ROUTE.SEARCH);
    };

    const sectionHookProps: SectionDependencies = {
      navigate,
      location,
      dispatch,
      competencies,
      doesUserHaveBlueprint: !!doesUserHaveBlueprint?.length || false,
      userPermissions: userPermissions || undefined,
      postHog,
      isUnauthenticated: user?.isUnauthenticated || false,
    };

    return {
      search: {
        onSearch: handleSearch,
        isPageActive: location.pathname === APP_ROUTE.SEARCH,
      },
      user,
      accountMenuItems: popupSectionMenuItems({ ...sectionHookProps }),
      sections: [
        { menuItems: topSection(sectionHookProps) },
        { menuItems: middleSection(sectionHookProps) },
        { menuItems: bottomSection(sectionHookProps) },
      ],
    };
  }, [
    navigate,
    location,
    dispatch,
    competencies,
    doesUserHaveBlueprint,
    userPermissions,
    user,
    postHog,
  ]);
};
